<template>
  <div class="mod-customer-statement">
    <el-form :inline="true" :model="query">
      <el-form-item label="客户" required>
        <el-input v-model="customerLabel" readonly size="small" style="width:220px" placeholder="请选择客户">
          <el-button slot="append" @click="customerSelectorVisible = true">选择</el-button>
        </el-input>
      </el-form-item>
      <el-form-item label="对账期间">
        <el-date-picker
          v-model="periodRange"
          type="daterange"
          size="small"
          value-format="yyyy-MM-dd"
          range-separator="至"
          start-placeholder="开始"
          end-placeholder="结束"
          style="width:260px"
        />
      </el-form-item>
      <el-form-item label="对账口径">
        <el-radio-group v-model="query.statementMode" size="small">
          <el-radio-button label="receivable">按销售应收</el-radio-button>
          <el-radio-button label="invoice">按销项发票</el-radio-button>
        </el-radio-group>
      </el-form-item>
      <el-form-item>
        <el-button v-if="isAuth('finance:customerStatement:list')" type="primary" size="small" @click="loadData">生成对账单</el-button>
        <el-button v-if="isAuth('finance:customerStatement:export')" type="primary" size="small" :disabled="!statement" @click="exportHandle">导出Excel</el-button>
        <el-button size="small" :disabled="!statement" @click="printHandle">打印</el-button>
      </el-form-item>
    </el-form>

    <div v-loading="loading" v-if="statement" id="customer-statement-print" class="statement-panel">
      <div class="statement-title">客户对账单</div>
      <div class="statement-meta">
        <span>客户：{{ statement.customerLabel }}</span>
        <span>期间：{{ fmtDate(statement.periodStart) }} 至 {{ fmtDate(statement.periodEnd) }}</span>
        <span>口径：{{ statement.statementModeLabel }}</span>
      </div>
      <el-row :gutter="12" class="statement-summary">
        <el-col :span="6"><div class="sum-box">期初余额<span>{{ fmtAmt(statement.openingBalance) }}</span></div></el-col>
        <el-col :span="6"><div class="sum-box">本期增加<span>{{ fmtAmt(statement.periodDebit) }}</span></div></el-col>
        <el-col :span="6"><div class="sum-box">本期减少<span>{{ fmtAmt(statement.periodCredit) }}</span></div></el-col>
        <el-col :span="6"><div class="sum-box highlight">期末应收<span>{{ fmtAmt(statement.closingBalance) }}</span></div></el-col>
      </el-row>

      <el-table
        :data="statement.lines"
        border
        size="small"
        :header-cell-style="{ background: '#f5f7fa', color: '#606266' }"
        :row-class-name="rowClassName"
      >
        <el-table-column prop="bizDate" label="日期" width="110" header-align="center" align="center">
          <template slot-scope="s">{{ fmtDate(s.row.bizDate) }}</template>
        </el-table-column>
        <el-table-column prop="bizType" label="单据类型" width="100" header-align="center" align="left" />
        <el-table-column prop="bizCode" label="单据号" min-width="140" header-align="center" align="left">
          <template slot-scope="s">
            <a v-if="canOpenStmtDoc(s.row)" class="code-link" @click="openStmtDoc(s.row)">{{ s.row.bizCode }}</a>
            <span v-else>{{ s.row.bizCode }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="remark" label="摘要" min-width="160" header-align="center" align="left" show-overflow-tooltip />
        <el-table-column prop="debitAmount" label="增加(应收)" width="120" header-align="center" align="right">
          <template slot-scope="s">{{ fmtAmt(s.row.debitAmount) }}</template>
        </el-table-column>
        <el-table-column prop="creditAmount" label="减少" width="120" header-align="center" align="right">
          <template slot-scope="s">{{ fmtAmt(s.row.creditAmount) }}</template>
        </el-table-column>
        <el-table-column prop="balanceAmount" label="余额" width="120" header-align="center" align="right">
          <template slot-scope="s">{{ fmtAmt(s.row.balanceAmount) }}</template>
        </el-table-column>
      </el-table>
      <p class="statement-foot">说明：增加为应收/发票金额，减少为收款、退货结算及红字冲减；期末余额为截至期间末尚未结清金额。</p>
    </div>

    <customer-selector :visible.sync="customerSelectorVisible" @select="onCustomerSelect" />
    <div v-show="false">
      <component :is="stmtPageComp" v-if="stmtPageComp" ref="stmtPage" />
    </div>
  </div>
</template>

<script>
import CustomerSelector from '../../components/CustomerSelector.vue'
import { docAction, docBlob } from '../../api/erp/doc'
import auth from './financeAuth'
import statementDocOpen from './statementDocOpen'

function monthRange() {
  const d = new Date()
  const start = new Date(d.getFullYear(), d.getMonth(), 1)
  const end = new Date(d.getFullYear(), d.getMonth() + 1, 0)
  const f = x => x.getFullYear() + '-' + String(x.getMonth() + 1).padStart(2, '0') + '-' + String(x.getDate()).padStart(2, '0')
  return [f(start), f(end)]
}

export default {
  mixins: [auth, statementDocOpen],
  components: { CustomerSelector },
  data() {
    const [ps, pe] = monthRange()
    return {
      query: { customerId: '', statementMode: 'receivable' },
      customerLabel: '',
      periodRange: [ps, pe],
      customerSelectorVisible: false,
      statement: null,
      statementPayload: null,
      statementRequestId: 0,
      loading: false
    }
  },
  watch: {
    query: { deep: true, handler() { this.invalidateStatement() } },
    periodRange: { deep: true, handler() { this.invalidateStatement() } }
  },
  methods: {
    invalidateStatement() {
      this.statementRequestId++
      this.statement = null
      this.statementPayload = null
      this.loading = false
    },
    onCustomerSelect(c) {
      if (!c) return
      this.query.customerId = c.customerId
      this.customerLabel = ((c.customerCode || '') + ' ' + (c.customerName || '')).trim()
    },
    payload() {
      return {
        partnerId: this.query.customerId,
        dateFrom: this.periodRange[0],
        dateTo: this.periodRange[1],
        statementMode: this.query.statementMode,
        includeSettled: true
      }
    },
    loadData() {
      if (!this.query.customerId) {
        this.$message.warning('请选择客户')
        return
      }
      if (!this.periodRange || this.periodRange.length !== 2) {
        this.$message.warning('请选择对账期间')
        return
      }
      const payload = this.payload()
      const customerLabel = this.customerLabel
      const requestId = ++this.statementRequestId
      this.statement = null
      this.statementPayload = null
      this.loading = true
      docAction('/finance/customerStatement', 'list', payload).then(res => {
        if (requestId !== this.statementRequestId) return
        const rows = res.data || []
        const open = rows.find(row => row.rowType === 'opening') || {}
        const close = rows.find(row => row.rowType === 'closing') || {}
        this.statement = {
          customerLabel,
          periodStart: payload.dateFrom,
          periodEnd: payload.dateTo,
          statementModeLabel: payload.statementMode === 'invoice' ? '按销项发票' : '按销售应收',
          openingBalance: open.amount,
          periodDebit: close.debitAmount,
          periodCredit: close.creditAmount,
          closingBalance: close.balanceAmount,
          lines: rows
        }
        this.statementPayload = payload
      }).catch(err => {
        if (requestId !== this.statementRequestId) return
        this.statement = null
        this.statementPayload = null
        this.$message.error((err && err.message) || '加载客户对账单失败')
      }).finally(() => { if (requestId === this.statementRequestId) this.loading = false })
    },
    exportHandle() {
      if (!this.statement || !this.statementPayload) return
      docBlob('/finance/customerStatement', 'export', this.statementPayload).then(res => {
        const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = (this.statement.customerLabel || '客户') + '_对账单.xlsx'
        a.click()
        window.URL.revokeObjectURL(url)
      }).catch(err => { this.$message.error((err && err.message) || '导出客户对账单失败') })
    },
    printHandle() { window.print() },
    fmtDate(v) { return v ? String(v).slice(0, 10) : '' },
    fmtAmt(v) { return Number(v || 0).toFixed(2) },
    rowClassName({ row }) {
      if (row.rowType === 'opening' || row.rowType === 'closing') return 'statement-summary-row'
      return ''
    }
  }
}
</script>

<style scoped>
.statement-panel { margin-top: 12px; padding: 16px; background: #fff; border: 1px solid #ebeef5; }
.statement-title { text-align: center; font-size: 18px; font-weight: 600; margin-bottom: 8px; }
.statement-meta { display: flex; flex-wrap: wrap; gap: 16px; margin-bottom: 12px; color: #606266; font-size: 13px; }
.statement-summary { margin-bottom: 12px; }
.sum-box { background: #f5f7fa; border-radius: 4px; padding: 10px 12px; font-size: 13px; color: #606266; }
.sum-box span { display: block; margin-top: 4px; font-size: 18px; font-weight: 600; color: #303133; }
.sum-box.highlight span { color: #e6a23c; }
.statement-foot { margin-top: 10px; font-size: 12px; color: #909399; }
.code-link { color: #348adb; cursor: pointer; }
>>> .statement-summary-row { background: #f0f9ff; font-weight: 600; }
@media print {
  .mod-customer-statement .el-form { display: none; }
  .statement-panel { border: none; padding: 0; }
}
</style>
